import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { MonthlyTourProgramStore } from "../../../../../store/main/MonthlyTourProgramStore";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import Swal from "sweetalert2";
import IMAGES from "../../../../../assets/images/images";

interface SmartFillModalProps {
  open: boolean;
  onClose: () => void;
  data: SmartFillSuggestions;
  loading: boolean;
  refreshData?: () => void; // ✅ add this
  closeParentModal?: () => void; // ✅ optional, for cleaner close
}

type SmartFillFlatEntry = {
  date?: string;
  type: string;
  working_with_id: number | null;
  hq_brick_id: number | null;
  hq_territory_id: number | null;
  hq_headquarter_id: number | null;
  originalKey?: string;
  __key?: string;
};
// ✅ New type
type SmartFillSuggestions = {
  [date: string]: SmartFillFlatEntry[];
};

interface FormValues {
  suggestions: SmartFillFlatEntry[];
}

const commonBorderStyle = {
  border: "none",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
};

const SmartFillModal: React.FC<SmartFillModalProps> = ({
  open,
  onClose,
  data,
  loading,
  closeParentModal,
  refreshData,
}) => {
  const { control, handleSubmit, watch, setValue, reset } = useForm<FormValues>(
    { defaultValues: { suggestions: [] } }
  );


  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      // flatten in ascending date order
      const flatData = Object.entries(data)
        .flatMap(([date, entries]) =>
          entries.map((entry) => ({ ...entry, date }))
        )
        .sort((a, b) => moment(a.date).diff(moment(b.date)));

      // ensure unique (defensive)
      const uniqueData = flatData.filter(
        (item, idx, arr) =>
          idx ===
          arr.findIndex(
            (t) =>
              t.date === item.date &&
              t.type === item.type &&
              (t.hq_brick_id ?? "") === (item.hq_brick_id ?? "")
          )
      );

      // add an originalIndex to each entry so we can map controllers correctly
      const withIndex = uniqueData.map((entry, i) => ({
        ...entry,
        __originalIndex: i, // IMPORTANT: used later in Controller names
        __key: `${entry.date}-${entry.type}-${i}`, // stable key for rendering
      }));

      // initialize the form with these entries
      reset({ suggestions: withIndex });

      // ensure saved/changed arrays match length
      setSavedRows(Array(withIndex.length).fill(false));
      setChangedRows(Array(withIndex.length).fill(false));
    } else {
      // if no data, clear form
      reset({ suggestions: [] });
      setSavedRows([]);
      setChangedRows([]);
    }
  }, [data, reset]);

  const { decodeToken, userDetails } = CustomerDatabaseStore();
  const {
    fetchUserFieldWorkChildren,
    fetchUserTerritory,
    fetchUserTerritoryBricks,
    fetchHeadQuarterListTour,
    addTourProgramForm,
  } = MonthlyTourProgramStore();

  const [workingWithData, setWorkingWithData] = useState<
    { id: number; name: string }[]
  >([]);
  const [territoryData, setTerritoryData] = useState<
    { id: number; name: string }[]
  >([]);
  const [brickData, setBrickData] = useState<
    { id: number; name: string; territory_id?: number }[]
  >([]);
  const [loadingDropdown, setLoadingDropdown] = useState(false);
  // store working_with data per row (since it depends on Field Work type)
  const [workingWithByRow, setWorkingWithByRow] = useState<
    Record<number, { id: number; name: string }[]>
  >({});
  const [brickDataByTerritory, setBrickDataByTerritory] = useState<
    Record<number, { id: number; name: string }[]>
  >({});
  const [savedRows, setSavedRows] = useState<boolean[]>([]);
  const [changedRows, setChangedRows] = useState<boolean[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalLoading, setModalLoading] = useState(true);

  useEffect(() => {
    if (open) {
      // when modal opens, wait until data is ready and render stabilizes
      const timer = setTimeout(() => setModalLoading(false), 300);
      return () => clearTimeout(timer);
    } else {
      setModalLoading(true);
    }
  }, [open, data]);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const initDropdowns = async () => {
      try {
        // Decode user once
        await decodeToken();

        // Wait until userDetails is populated before proceeding
        if (!userDetails?.id) {
          console.warn("User details not ready yet");
          return;
        }

        // Fetch territory list only once
        await loadTerritory();

        if (isMounted && data && Object.keys(data).length > 0) {
          const allEntries = Object.values(data).flat();
          const uniqueTerritoryIds = new Set<number>();
          let hasFieldWork = false;

          allEntries.forEach((entry) => {
            if (entry.hq_territory_id) {
              uniqueTerritoryIds.add(Number(entry.hq_territory_id));
            }
            if (entry.type === "Field Work") {
              hasFieldWork = true;
            }
          });

          // ✅ Load all bricks for existing territories
          await Promise.all(
            Array.from(uniqueTerritoryIds).map((tid) => loadBricks(tid))
          );

          // ✅ Load working-with list if needed
          if (hasFieldWork) {
            const res = await fetchUserFieldWorkChildren({
              role_id: "3",
              status: "active",
              ...(Number(userDetails?.role_id) < 5
                ? { manager_id: userDetails?.id }
                : {}),
            });

            const users = Array.isArray(res?.users) ? res.users : [];
            const mappedUsers = [
              { id: 0, name: "Working Alone" },
              ...users.map((u: any) => ({
                id: u.id,
                name: u.full_name || "Unknown",
              })),
            ];

            const workingWithMap: Record<
              number,
              { id: number; name: string }[]
            > = {};
            allEntries.forEach((entry, index) => {
              if (entry.type === "Field Work") {
                workingWithMap[index] = mappedUsers;
                setValue(
                  `suggestions.${index}.working_with_id`,
                  entry.working_with_id ?? null
                );
              }
            });
            setWorkingWithByRow(workingWithMap);
          }
        }
      } catch (err) {
        console.error("Error initializing dropdowns:", err);
      }
    };

    initDropdowns();

    return () => {
      isMounted = false;
    };
  }, [open]);

  const loadTerritory = async () => {
    try {
      setLoadingDropdown(true);
      const res = await fetchHeadQuarterListTour();
      const territories = Array.isArray(res?.headquarters)
        ? res.headquarters
        : [];
      setTerritoryData(
        territories.map((t: any) => ({
          id: t.id,
          name: t.name || "Unknown",
        }))
      );
    } catch {
      setTerritoryData([]);
    } finally {
      setLoadingDropdown(false);
    }
  };

  const loadWorkingWith = async (rowIndex: number) => {
    try {
      setLoadingDropdown(true);
      const payload: any = {
        role_id: "3",
        status: "active",
      };
      if (Number(userDetails?.role_id) < 5)
        payload.manager_id = userDetails?.id;

      const res = await fetchUserFieldWorkChildren(payload);
      const users = Array.isArray(res?.users) ? res.users : [];

      // ✅ Add "Working Alone" as the first option
      const mappedUsers = [
        { id: 0, name: "Working Alone" },
        ...users.map((u: any) => ({
          id: u.id,
          name: u.full_name || "Unknown",
        })),
      ];

      setWorkingWithByRow((prev) => ({ ...prev, [rowIndex]: mappedUsers }));

      // ✅ Handle preselected value if valid
      const preSelectedId = watchedTours[rowIndex]?.working_with_id;
      if (preSelectedId && mappedUsers.some((u) => u.id === preSelectedId)) {
        setValue(`suggestions.${rowIndex}.working_with_id`, preSelectedId);
      } else {
        setValue(`suggestions.${rowIndex}.working_with_id`, null);
      }
    } catch (err) {
      console.error("Error loading working with:", err);
      setWorkingWithByRow((prev) => ({ ...prev, [rowIndex]: [] }));
    } finally {
      setLoadingDropdown(false);
    }
  };

  const loadBricks = async (territoryId: number) => {
    try {
      setLoadingDropdown(true);
      const res = await fetchUserTerritoryBricks({ territory_id: territoryId });
      const bricks = Array.isArray(res?.bricks) ? res.bricks : [];
      setBrickDataByTerritory((prev) => ({
        ...prev,
        [territoryId]: bricks.map((b: any) => ({
          id: b.id,
          name: b.name || "Unknown",
        })),
      }));
    } catch (err) {
      console.error("Error loading bricks:", err);
      setBrickDataByTerritory((prev) => ({ ...prev, [territoryId]: [] }));
    } finally {
      setLoadingDropdown(false);
    }
  };

  const { fields } = useFieldArray({ control, name: "suggestions" });
  const watchedTours = watch("suggestions");

  // stores all confirmed rows (after clicking tick)
  const [confirmedChanges, setConfirmedChanges] = useState<
    SmartFillFlatEntry[]
  >([]);

  const groupByWeek = (
    tours: (SmartFillFlatEntry & { __originalIndex?: number })[]
  ) => {
    if (!tours || tours.length === 0) return [];

    // already sorted in useEffect, but safe to sort again
    const sorted = [...tours].sort((a, b) =>
      moment(a.date).diff(moment(b.date))
    );

    const grouped: Record<string, (typeof sorted)[0][]> = {};

    sorted.forEach((t) => {
      const weekNum = moment(t.date).isoWeek();
      // optionally include year to avoid collisions across years
      const year = moment(t.date).year();
      const key = `Week ${weekNum} ${year}`; // internal key

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(t);
    });

    // map to array with a nicer label (you can compute range later)
    return Object.entries(grouped).map(([k, items]) => ({
      weekKey: k,
      weekLabel: k.replace(/\s\d{4}$/, ""), // "Week X", remove year if you like
      data: items,
    }));
  };

  const onSave = (index: number, rowDate: string) => {
    const currentValues = watch(`suggestions.${index}`);

    setSavedRows((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });

    // ✅ Ensure correct date is used (not index-based)
    const updatedRow = { ...currentValues, date: rowDate };

    setConfirmedChanges((prev) => {
      const alreadyExists = prev.find((item) => item.date === rowDate);

      if (alreadyExists) {
        // update existing row if ticked again after changes
        return prev.map((item) => (item.date === rowDate ? updatedRow : item));
      }

      // add if ticked for first time
      return [...prev, updatedRow];
    });
  };

  useEffect(() => {
    if (!open) {
      // Reset tick icons and confirmed data when modal closes
      // setSavedRows(Array(flatData.length).fill(false));
      setConfirmedChanges([]);
    }
  }, [open]);

  const onSubmit = async () => {
    if (isSubmitting) return; // prevent multiple clicks
    setIsSubmitting(true);

    const allRows = watch("suggestions");

    const mergedRows = allRows.map((row) => {
      const confirmed = confirmedChanges.find((c) => c.date === row.date);
      return confirmed ? { ...row, ...confirmed } : row;
    });

    const payload = {
      tours: mergedRows.map((row) => ({
        date: row.date,
        hq_brick_id: row.hq_brick_id,
        type: row.type,
        user_id: userDetails?.id,
        working_with_id: row.working_with_id,
      })),
    };


    try {
      const res = await addTourProgramForm(payload);
      if (res?.success) {
        // ✅ Build success and error messages
        const createdCount = res.created || 0;
        let message = "";

        if (createdCount > 0) {
          message += `✅ ${createdCount} tour${
            createdCount > 1 ? "s" : ""
          } created successfully.\n`;
        }

        if (res.errors && Array.isArray(res.errors) && res.errors.length > 0) {
          const errorMessages = res.errors
            .map(
              (err: any) =>
                `📅 ${moment(err.date).format("D MMM YYYY")} - ${err.message}`
            )
            .join("\n");
          message += `\n⚠️ Some issues occurred:\n${errorMessages}`;
        }

        // ✅ Always close the modal before showing message
        onClose();
        closeParentModal?.();
        await refreshData?.();

        // ✅ Show combined SweetAlert
        await Swal.fire({
          title: "Smart Fill Result",
          text: message.trim(),
          icon: res.errors?.length ? "warning" : "success",
          confirmButtonColor: "#1976d2",
          customClass: {
            popup: "swal-wide",
          },
        });
      } else {
        // ❌ Failure case
        onClose();
        await Swal.fire({
          title: "Failed",
          text: res?.message || "Something went wrong while updating.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    } catch (err) {
      console.error("Error updating records:", err);
      onClose();
      await Swal.fire({
        title: "Error",
        text: "Failed to update tour program. Please try again.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          width: "80%",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          p: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <img
            src={IMAGES.SmartFill}
            alt="Smart Fill"
            style={{ width: 40, height: 36, objectFit: "contain" }}
          />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Smart Fill
            </Typography>
            <Typography variant="body2">
              Analyzes your data from previous months to generate
              recommendations for the current month.
            </Typography>
          </Box>
        </Box>

        {loading || modalLoading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : fields.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
          >
            <Typography variant="h6" color="text.secondary">
              Tour is not available currently
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Day",
                    "Type",
                    "Territory",
                    "Working With",
                    "Brick",
                    // "Actions",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      align="center"
                      sx={{ fontWeight: 600, py: 0.5 }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {groupByWeek(fields).map((week, weekIndex) => (
                  <React.Fragment key={week.weekKey}>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ fontWeight: 600, py: 1, bgcolor: "grey.200" }}
                      >
                        {week.weekLabel}
                      </TableCell>
                    </TableRow>

                    {week.data.map((field) => {
                      // use original index (must exist from flatten/reset)
                      const index =
                        field.__originalIndex ??
                        fields.findIndex(
                          (f) => f.date === field.date && f.type === field.type
                        );
                      const selectedType = watchedTours[index]?.type;
                      const isFieldWork = selectedType === "Field Work";
                      const territoryId =
                        watchedTours[index]?.hq_territory_id ?? null;

                      return (
                        <TableRow
                          key={`${field.date}-${index}`}
                          sx={{ "& td": { py: 0.3 } }}
                        >
                          <TableCell align="center">
                            {moment(field.date).format("D MMM")}
                          </TableCell>

                          {/* Type */}
                          <TableCell align="center">
                            <Controller
                              name={`suggestions.${index}.type`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 180 }}
                                >
                                  <Select
                                    {...field}
                                    sx={commonBorderStyle}
                                    // onChange={async (event) => {
                                    //   const newValue = event.target.value;

                                    //   if (newValue !== field.value) {
                                    //     field.onChange(event);

                                    //     // reset dependent fields
                                    //     setValue(
                                    //       `suggestions.${index}.working_with_id`,
                                    //       null
                                    //     );
                                    //     setValue(
                                    //       `suggestions.${index}.hq_territory_id`,
                                    //       null
                                    //     );
                                    //     setValue(
                                    //       `suggestions.${index}.hq_brick_id`,
                                    //       null
                                    //     );

                                    //     setChangedRows((prev) => {
                                    //       const updated = [...prev];
                                    //       updated[index] = true;
                                    //       return updated;
                                    //     });
                                    //     setSavedRows((prev) => {
                                    //       const updated = [...prev];
                                    //       updated[index] = false;
                                    //       return updated;
                                    //     });

                                    //     // ✅ Fetch Working With list if type changes to Field Work
                                    //     if (newValue === "Field Work") {
                                    //       await loadWorkingWith(index);
                                    //     } else {
                                    //       setWorkingWithByRow((prev) => ({
                                    //         ...prev,
                                    //         [index]: [],
                                    //       }));
                                    //     }
                                    //   }
                                    // }}

                                    onChange={async (event) => {
  const newValue = event.target.value;

  if (newValue !== field.value) {
    field.onChange(event);

    // ✅ Always reset dependent fields
    setValue(`suggestions.${index}.working_with_id`, null);
    setValue(`suggestions.${index}.hq_territory_id`, null);
    setValue(`suggestions.${index}.hq_brick_id`, null);

    // ✅ Clear related dropdown data when not Field Work
    if (newValue !== "Field Work") {
      setWorkingWithByRow((prev) => ({
        ...prev,
        [index]: [],
      }));
      setBrickDataByTerritory((prev) => ({
        ...prev,
        [index]: [],
      }));
    }

    // ✅ Mark as changed (for your save tick)
    setChangedRows((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
    setSavedRows((prev) => {
      const updated = [...prev];
      updated[index] = false;
      return updated;
    });

    // ✅ Only load Working With list if “Field Work” is selected
    if (newValue === "Field Work") {
      await loadWorkingWith(index);
    }
  }
}}

                                  >
                                    <MenuItem value="Meeting">Meeting</MenuItem>
                                    <MenuItem value="Field Work">
                                      Field Work
                                    </MenuItem>
                                    <MenuItem value="Office Work">
                                      Office Work
                                    </MenuItem>
                                    <MenuItem value="Transit">Transit</MenuItem>
                                    <MenuItem value="leave">Leave</MenuItem>
                                    <MenuItem value="holiday">Holiday</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </TableCell>

                          {/* Territory */}
                          {/* Territory */}
                          <TableCell align="center">
                            <Controller
                              name={`suggestions.${index}.hq_territory_id`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 180 }}
                                >
                                  <Select
                                    value={field.value ?? ""}
                                    sx={commonBorderStyle}
                                    disabled={!isFieldWork}
                                    onChange={async (e) => {
                                      const rawValue = e.target.value;
                                      const tid = rawValue
                                        ? Number(rawValue)
                                        : null;

                                      // Store number instead of string
                                      field.onChange(tid);

                                      // Clear old bricks if territory changed
                                      setValue(
                                        `suggestions.${index}.hq_brick_id`,
                                        null
                                      );

                                      if (tid) {
                                        await loadBricks(tid);
                                      } else {
                                        // Clear brick data if invalid territory
                                        setBrickDataByTerritory((prev) => ({
                                          ...prev,
                                          [tid ?? 0]: [],
                                        }));
                                      }

                                      // Mark row as changed
                                      setChangedRows((prev) => {
                                        const updated = [...prev];
                                        updated[index] = true;
                                        return updated;
                                      });
                                      setSavedRows((prev) => {
                                        const updated = [...prev];
                                        updated[index] = false;
                                        return updated;
                                      });
                                    }}
                                  >
                                    {territoryData.length > 0 ? (
                                      territoryData.map((option) => (
                                        <MenuItem
                                          key={option.id}
                                          value={option.id}
                                        >
                                          {option.name}
                                        </MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem disabled>
                                        {loadingDropdown
                                          ? "Loading..."
                                          : "No data"}
                                      </MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </TableCell>
                          {/* Working With */}
                          <TableCell align="center">
                            <Controller
                              name={`suggestions.${index}.working_with_id`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 180 }}
                                >
                                  <Select
                                    {...field}
                                    sx={commonBorderStyle}
                                    disabled={!isFieldWork}
                                  >
                                    {isFieldWork &&
                                    (workingWithByRow[index]?.length ?? 0) >
                                      0 ? (
                                      workingWithByRow[index].map((option) => (
                                        <MenuItem
                                          key={option.id}
                                          value={option.id}
                                        >
                                          {option.name}
                                        </MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem disabled>
                                        {isFieldWork
                                          ? loadingDropdown
                                            ? "Loading..."
                                            : "No data"
                                          : "Select Field Work"}
                                      </MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </TableCell>

                          {/* Brick */}
                          <TableCell align="center">
                            <Controller
                              name={`suggestions.${index}.hq_brick_id`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 180 }}
                                >
                                  <Select
                                    {...field}
                                    sx={commonBorderStyle}
                                    disabled={!isFieldWork || !territoryId}
                                  >
                                    {(
                                      brickDataByTerritory[territoryId ?? 0] ||
                                      []
                                    ).length > 0 ? (
                                      brickDataByTerritory[
                                        territoryId ?? 0
                                      ].map((option) => (
                                        <MenuItem
                                          key={option.id}
                                          value={option.id}
                                        >
                                          {option.name}
                                        </MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem disabled>
                                        {loadingDropdown
                                          ? "Loading..."
                                          : "No Bricks"}
                                      </MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Footer */}
        <Box mt={3} display="flex" justifyContent="center" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting} // ✅ disable when submitting
          >
            {isSubmitting ? "Updating" : "Update Records"}
          </Button>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel & Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SmartFillModal;
